import { useSort } from '@haus-storefront-react/product-list'
import * as Select from '@radix-ui/react-select'
import type { SearchResultSortParameter } from '@haus-storefront-react/shared-types'
import { ChevronDownIcon, CheckIcon } from '@radix-ui/react-icons'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AriaLiveRegion, useWidgetReadyAfterInitialLoad, WidgetReadyBoundary } from '@components'
import styles from './Sort.module.scss'
import './SortSkeleton.css'

export interface SortOption {
  labelKey: string
  value: SearchResultSortParameter
}

interface SortProps {
  productListIdentifier: string
  sortOptions: SortOption[]
}

export default function SortComponent({ productListIdentifier, sortOptions }: SortProps) {
  const { t } = useTranslation()
  const [sortAnnouncement, setSortAnnouncement] = useState('')
  const sortDescriptionId = useId()
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent: true,
    hasVisibleContent: sortOptions.length > 0,
    isLoading: false,
  })

  const { currentSortValue, handleSortOptionChange } = useSort({
    productListIdentifier,
  })

  if (sortOptions.length === 0) {
    return null
  }

  const currentSortOption = sortOptions.find(
    (option) => JSON.stringify(option.value) === JSON.stringify(currentSortValue),
  )

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <AriaLiveRegion message={sortAnnouncement} />
      <span id={sortDescriptionId} className={styles.srOnly}>
        {t('a11y.sort.aria.description')}
      </span>
      <Select.Root
        value={currentSortOption?.labelKey || ''}
        onValueChange={(value) => {
          const selectedOption = sortOptions.find((option) => option.labelKey === value)
          if (selectedOption) {
            handleSortOptionChange(selectedOption.value)
            setSortAnnouncement(
              t('a11y.sort.ariaLive.sortUpdatedTo', {
                selectedSortLabel: t(selectedOption.labelKey),
              }),
            )
          }
        }}
      >
        <Select.Trigger
          className={styles.sortButton}
          aria-label={t('a11y.sort.aria.sortProducts')}
          aria-describedby={sortDescriptionId}
        >
          <Select.Value placeholder={t('sort.sortBy')}>
            <span className={styles.sortByLabel}>{t('sort.sortBy')}:</span>{' '}
            <span className={styles.sortByValue}>
              {currentSortOption ? t(currentSortOption.labelKey) : ''}
            </span>
          </Select.Value>
          <ChevronDownIcon width={16} height={16} aria-hidden="true" />
        </Select.Trigger>
        <Select.Content
          className={styles.dropdownContent}
          position="popper"
          sideOffset={6}
          align="start"
          aria-label={t('a11y.sort.aria.sortOptions')}
        >
          <Select.Viewport className={styles.selectViewport}>
            {sortOptions.map((option, index) => {
              const isSelected = currentSortOption?.labelKey === option.labelKey
              const label = t(option.labelKey)
              return (
                <Select.Item
                  key={index}
                  value={option.labelKey}
                  className={styles.selectItem}
                  aria-label={t('a11y.sort.aria.sortByOption', { sortOptionLabel: label })}
                  aria-selected={isSelected}
                >
                  <div className={styles.checkIconSpacer}>
                    <Select.ItemIndicator className={styles.itemIndicator} aria-hidden="true">
                      <CheckIcon width={16} height={16} />
                    </Select.ItemIndicator>
                  </div>
                  <Select.ItemText>{label}</Select.ItemText>
                </Select.Item>
              )
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
    </WidgetReadyBoundary>
  )
}
